import { Box } from '@chakra-ui/react';
import { FC, useMemo } from 'react';

import IconNext from '@/assets/icons/ic-arrow-next.svg';
import IconPrev from '@/assets/icons/ic-arrow-prev.svg';

import { getDaysButtons } from '@/components/ui/features/schedule/ScheduleCalendar/ScheduleCalendar.utils';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';

interface IUrlQueryStringParams {
  day?: string;
}

export interface IScheduleCalendarDayNavigationProps {
  days: Array<string>;
  timeZone: string;
  isDesktop: boolean;
}

export const ScheduleCalendarDayNavigation: FC<IScheduleCalendarDayNavigationProps> = ({
  days,
  timeZone,
  isDesktop,
}) => {
  const { day: activeDay, change } = useQueryParams<IUrlQueryStringParams>({
    day: StringParam,
  });

  const handleOnPrevClick = () => {
    const targetDayIndex = days.findIndex((day) => day === activeDay);

    if (targetDayIndex === -1 || targetDayIndex - 1 < 0) {
      return;
    }

    change({ day: days[targetDayIndex - 1] }, { method: 'replace', shallow: true });
  };

  const handleOnNextClick = () => {
    const targetDayIndex = days.findIndex((day) => day === activeDay);

    if (targetDayIndex === -1 || targetDayIndex + 1 > days.length) {
      return;
    }

    change({ day: days[targetDayIndex + 1] }, { method: 'replace', shallow: true });
  };

  const handleOnDayClick = (buttonId) => {
    if (!buttonId || buttonId === activeDay) {
      return;
    }

    change({ day: buttonId }, { method: 'replace', shallow: true });
  };

  const activeDayIndex = useMemo(() => days?.findIndex?.((day) => day === activeDay), [
    days,
    activeDay,
    timeZone,
    isDesktop,
  ]);

  const daysButtons = useMemo(() => getDaysButtons(days, activeDay), [days, activeDay]);

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" mb={{ base: 2, lg: 0 }}>
      {activeDayIndex > 0 && (
        <Box w="14px" h="14px" mr={3} mt={{ sm: 0, md: 3 }} cursor="pointer" onClick={handleOnPrevClick}>
          <IconPrev />
        </Box>
      )}
      {daysButtons.length > 0 &&
        daysButtons.map((item, index) => {
          return (
            <Box key={index} mx={3} px={{ sm: 0, lg: 4 }}>
              <Box
                cursor="pointer"
                onClick={() => handleOnDayClick(item.id)}
                userSelect="none"
                fontWeight="bold"
                fontFamily="heading"
                fontSize={{ base: 'xs', sm: 'sm', md: '2xl', lg: '3xl' }}
                color={item.id === activeDay ? 'defaultText' : 'gray.400'}
              >
                {item.text}
              </Box>
            </Box>
          );
        })}
      {days && activeDayIndex < days?.length - 1 && (
        <Box w="14px" h="14px" ml={3} mt={{ base: 0, md: 3 }} cursor="pointer" onClick={handleOnNextClick}>
          <IconNext />
        </Box>
      )}
    </Box>
  );
};
